import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Minus, Plus } from '../ui/Icon';

interface Props {
  /** The box's size now, in pt. Remount (key) on a new one to drop a half-typed value. */
  pt: number;
  /** ADR-0053: the catalogue's range for the box's kind; nothing outside is offered. */
  range: { min: number; max: number };
  disabled: boolean;
  onChange: (pt: number) => void;
}

const clamp = (v: number, { min, max }: Props['range']) => Math.min(max, Math.max(min, Math.round(v * 2) / 2));

/** − / the size in pt / + and a slider. The slider and the field save when let go, not on every step. */
export default function AlbumTextSizeControl({ pt, range, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(String(pt));
  const commit = (value: number) => {
    if (Number.isNaN(value)) { setDraft(String(pt)); return; }
    const next = clamp(value, range);
    setDraft(String(next));
    if (next !== pt) onChange(next);
  };
  const button = 'w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer text-slate-600 hover:bg-white disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5 w-fit">
        <button type="button" className={button} disabled={disabled || pt <= range.min} onClick={() => commit(pt - 1)} aria-label={t('album.style.sizeDown')}>
          <Icon as={Minus} size={13} />
        </button>
        <label className="flex items-baseline gap-0.5 px-1">
          <input
            type="number"
            inputMode="decimal"
            min={range.min}
            max={range.max}
            step={0.5}
            value={draft}
            disabled={disabled}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => commit(parseFloat(draft))}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            aria-label={t('album.style.textSize')}
            className="w-11 bg-transparent text-center text-xs font-semibold tabular-nums text-slate-900 dark:text-slate-100 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-[10px] text-slate-500">pt</span>
        </label>
        <button type="button" className={button} disabled={disabled || pt >= range.max} onClick={() => commit(pt + 1)} aria-label={t('album.style.sizeUp')}>
          <Icon as={Plus} size={13} />
        </button>
      </div>
      <input
        type="range"
        min={range.min}
        max={range.max}
        step={0.5}
        value={Number.isNaN(parseFloat(draft)) ? pt : parseFloat(draft)}
        disabled={disabled}
        onChange={e => setDraft(e.target.value)}
        onPointerUp={e => commit(parseFloat(e.currentTarget.value))}
        onKeyUp={e => commit(parseFloat(e.currentTarget.value))}
        aria-label={t('album.style.textSize')}
        className="w-full accent-teal-600 cursor-pointer"
      />
    </div>
  );
}
